import { S3Handler } from "../utils/s3";
import {
    createEvent,
    createEventTicket,
    getAccountIdByHandler,
    getEventByHandler,
    getEventOrdersOfEvent,
    fetchEventTickets,
    getUserEventsOfEvent,
    updateEvent,
    updateEventTicket,
    getEventCoupons,
    getUtmsOfEvent,
    fetchEventTicketsSecure,
    getLinksOfEventsSecured,
    getLinksOfEventsByUtm,
    getOrderPrice
} from "../api";
import { isDateGreaterThanToday, isDateLessThanToday } from "../utils/minimal/formatTime";
import * as API from "../api";
import { cleanExternalVariants } from "../pages/common/voucher/voucherRedemption";
import { FeeCollectionTypes } from "../consts/index.js";
import { ErrorService } from "./errorService.js";
import { SELLER_PAGE_IMAGE_FIT_MODES } from "../utils/sellerPageImageFit.js";
import { i18n } from '../locales'
import { DEFAULT_MAX_PER_CATEGORY } from "../utils/seatsMaxSelection";
import { UserEventService } from './userEventService';
import { combineDayAndTime } from "../utils/eventDateTime";
import { isBuyerHiddenTicket } from '../consts/index.js';


const toNoonUTC = (date) => {
    if (!date) return date;
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0, 0));
}

export const EventService = {
    async handleUpdateEventFlow(accountId, eventId, eventData, eventOriginalDesign) {
        const { event_main_photo, event_cover_photo, name, location, contact_email, event_handler, facebook_page, event_start_date, start_hour, end_hour, event_end_date, content, tickets, isSeatsEvent, feeCollectionType, is_manual_approve_buyer, facebook_pixel, google_analytics_id, embedded_html, maxCapacity, isWellnessEvent, trainer, type, isOnlyForMembers, collectSeparateBuyerDetails, otpGateMode, font, fontUrl, titleFont, titleFontUrl, FAB_text, show_FAB, isBookable, maxInstallments, termsPdfUrl, termsPdfFilename, termsRequired, incomeTypeId, incomeTypeName, sellerPageImageFit } = eventData

        if (event_main_photo?.shouldRenew && !event_main_photo.finalPath) {
            event_main_photo.finalPath = await S3Handler.uploadFile(event_main_photo)
        }

        if (event_cover_photo?.shouldRenew && !event_cover_photo.finalPath) {
            event_cover_photo.finalPath = await S3Handler.uploadFile(event_cover_photo)
        }

        const design = { image: event_main_photo.finalPath || event_main_photo.preview }
        const { colors } = getDesignColorsFromEventData(eventData)
        design.colors = colors

        design.imageWidth = event_main_photo.imageWidth ? event_main_photo.imageWidth : eventOriginalDesign.imageWidth
        design.imageHeight = event_main_photo.imageHeight ? event_main_photo.imageHeight : eventOriginalDesign.imageHeight

        // Only supported values persist ('inherit'/unknown omit the key); design is rebuilt
        // wholesale, so omitting also clears a saved value
        if (SELLER_PAGE_IMAGE_FIT_MODES.includes(sellerPageImageFit)) design.sellerPageImageFit = sellerPageImageFit

        if (event_cover_photo) {
            design.background = event_cover_photo.finalPath || event_cover_photo.preview
        }

        const eventDataToSend = {
            location,
            facebook_page,
            facebook_pixel,
            google_analytics_id,
            embedded_html,
            maxCapacity,
            contact_email,
            design,
            name,
            start_hour: start_hour ? combineDayAndTime(toNoonUTC(event_start_date), start_hour) : start_hour,
            end_hour: end_hour ? combineDayAndTime(toNoonUTC(event_end_date), end_hour) : end_hour,
            isSeatsEvent,
            feeCollectionType,
            is_manual_approve_buyer,
            handler: event_handler,
            description: content,
            start_date: toNoonUTC(event_start_date),
            end_date: toNoonUTC(event_end_date),
            type,
            isWellnessEvent,
            trainer,
            isOnlyForMembers,
            collectSeparateBuyerDetails,
            otpGateMode,
            font,
            fontUrl,
            titleFont,
            titleFontUrl,
            FAB_text,
            show_FAB,
            isBookable,
            maxInstallments,
            termsPdfUrl: termsPdfUrl ?? null,
            termsPdfFilename: termsPdfFilename ?? null,
            termsRequired: termsRequired ?? false,
            incomeTypeId: incomeTypeId ?? null,
            incomeTypeName: incomeTypeName ?? null,
        }

        const updatedEventResponse = await updateEvent({ accountId, eventId, eventData: eventDataToSend })
        let allTicketsResponse = []
        if (!isSeatsEvent) {
            const ticketsPromises = tickets.map((ticket) => {
                if (ticket.ticketId) {
                    const { ticketId } = ticket
                    delete ticket.ticketId
                    const ticketToUpdate = {
                        name: ticket.name,
                        quantity: ticket.quantity,
                        price: ticket.price,
                        countTicketsAllowPerPurchase: ticket.countTicketsAllowPerPurchase,
                        minTicketsAllowPerPurchase: ticket.minTicketsAllowPerPurchase || 0,
                        is_enabled: ticket.is_active,
                        start_date: ticket.active_from_date,
                        end_date: ticket.inactive_from_date,
                        order: ticket.order,
                        // Benefit-club variant mapping — cleaned (trim, drop incomplete, dedup).
                        externalVariants: cleanExternalVariants(ticket.externalVariants),
                    }
                    return updateEventTicket({ accountId, eventId, ticketId, ticketData: ticketToUpdate }).then(({ data }) => data)
                }
                const ticketDataToCreate = {
                    name: ticket.name,
                    quantity: ticket.quantity,
                    price: ticket.price,
                    is_enabled: ticket.is_active,
                    start_date: ticket.active_from_date,
                    end_date: ticket.inactive_from_date,
                    countTicketsAllowPerPurchase: ticket.countTicketsAllowPerPurchase,
                    minTicketsAllowPerPurchase: ticket.minTicketsAllowPerPurchase || 0,
                    description: "_",
                    currency: "ILS",
                    category: "party",
                    is_sold_out: false,
                    is_deleted: false,
                    is_hidden: false,
                    is_free: false,
                    is_recurring: false,
                    externalVariants: cleanExternalVariants(ticket.externalVariants),
                }
                return createEventTicket({ accountId, eventId, ticketData: ticketDataToCreate }).then(({ data }) => data)
            })
            allTicketsResponse = await Promise.all(ticketsPromises)
        }


        return {
            event: updatedEventResponse.data,
            tickets: allTicketsResponse
        }

    },
    async handleCreateEventFlow(accountId, sellerId, eventData) {
        if (!sellerId) {
            throw new Error('sellerId is not found')
        }

        const { event_main_photo, event_cover_photo, name, location, contact_email, event_handler, facebook_page, facebook_pixel, google_analytics_id, embedded_html, maxCapacity, event_start_date, start_hour, end_hour, event_end_date, content, tickets, isSeatsEvent, isAllowSeasonTicket, is_manual_approve_buyer, feeCollectionType, isWellnessEvent, trainer, type, isOnlyForMembers, collectSeparateBuyerDetails, otpGateMode, font, fontUrl, titleFont, titleFontUrl, FAB_text, show_FAB, isBookable, maxInstallments, termsPdfUrl, termsPdfFilename, termsRequired, incomeTypeId, incomeTypeName, sellerPageImageFit } = eventData

        if (!event_main_photo.finalPath) {
            event_main_photo.finalPath = await S3Handler.uploadFile(event_main_photo)
        }

        if (event_cover_photo && !event_cover_photo.finalPath) {
            event_cover_photo.finalPath = await S3Handler.uploadFile(event_cover_photo)
        }

        const { eventBackground, eventButtons, eventProgressBar, eventPrivacyPolicy } = eventData
        const design = { image: event_main_photo.finalPath, }

        const { colors } = getDesignColorsFromEventData(eventData)

        design.colors = colors

        if (event_main_photo.imageWidth) {
            design.imageWidth = event_main_photo.imageWidth
        }

        if (event_main_photo.imageHeight) {
            design.imageHeight = event_main_photo.imageHeight
        }

        // Only supported values persist — the BE enum accepts only cover/top/contain
        if (SELLER_PAGE_IMAGE_FIT_MODES.includes(sellerPageImageFit)) design.sellerPageImageFit = sellerPageImageFit

        if (event_cover_photo) {
            design.background = event_cover_photo.finalPath
        }

        const eventDataToSend = {
            name,
            location,
            facebook_page,
            facebook_pixel,
            google_analytics_id,
            embedded_html,
            maxCapacity,
            contact_email,
            design,
            start_hour: start_hour ? combineDayAndTime(toNoonUTC(event_start_date), start_hour) : start_hour,
            end_hour: end_hour ? combineDayAndTime(toNoonUTC(event_end_date), end_hour) : end_hour,
            isSeatsEvent,
            handler: event_handler,
            description: content,
            start_date: toNoonUTC(event_start_date),
            end_date: toNoonUTC(event_end_date),
            is_password_enabled: false,
            is_enabled: true,
            // New events start hidden from the seller page; the owner opts in later
            // via Settings → General → "Show in seller page" (isPublic). The create
            // form has no visibility toggle, so set it deterministically here rather
            // than relying on the backend default.
            isPublic: false,
            type,
            seller: sellerId,
            isAllowSeasonTicket,
            is_manual_approve_buyer,
            feeCollectionType,
            isWellnessEvent,
            trainer,
            isOnlyForMembers,
            collectSeparateBuyerDetails,
            otpGateMode,
            font,
            fontUrl,
            titleFont,
            titleFontUrl,
            FAB_text,
            show_FAB,
            isBookable,
            maxInstallments,
            termsPdfUrl: termsPdfUrl ?? null,
            termsPdfFilename: termsPdfFilename ?? null,
            termsRequired: termsRequired ?? false,
            incomeTypeId: incomeTypeId ?? null,
            incomeTypeName: incomeTypeName ?? null,
        }

        const newEventResponse = await createEvent({ accountId, eventData: eventDataToSend })

        let allTicketsResponse = []
        // if (!isSeatsEvent) {
        //     const promises = tickets.map(ticket => {
        //         const ticketData = {
        //             name: ticket.name,
        //             quantity: ticket.quantity,
        //             price: ticket.price,
        //             is_enabled: ticket.is_active,
        //             start_date: ticket.active_from_date,
        //             end_date: ticket.inactive_from_date,
        //             countTicketsAllowPerPurchase: ticket.countTicketsAllowPerPurchase,
        //             order: ticket.order,
        //             description: "_",
        //             currency: "ILS",
        //             category: "party",
        //             is_sold_out: false,
        //             is_deleted: false,
        //             is_hidden: false,
        //             is_free: false,
        //             is_recurring: false
        //         }
        //         return createEventTicket({ accountId, eventId: newEventResponse.data._id, ticketData })
        //     })
        //     allTicketsResponse = await Promise.all(promises)
        // }

        return {
            event: newEventResponse.data,
            tickets: allTicketsResponse.map(({ data }) => data)
        }
    },
    async handleDuplicateEventFlow(accountId, eventToDuplicate, duplicateEventHandlerName, duplicateEventName, duplicateEventStartDate) {

        const newEvent = {
            ...eventToDuplicate,
            name: duplicateEventName,
            handler: duplicateEventHandlerName,
            start_date: duplicateEventStartDate || eventToDuplicate.start_date,
            collaborates: [],
            links: [],
            // Start the duplicate hidden from the seller page regardless of the
            // source's visibility — owners duplicate to tweak, and shouldn't have
            // the copy publicly listed before they're ready. They re-enable it via
            // Settings → General → "Show in seller page" (isPublic).
            isPublic: false,
        }

        delete newEvent._id
        delete newEvent.updatedAt
        delete newEvent.createdAt
        delete newEvent.seatsEventKey
        delete newEvent.seatsChartKey
        // Operational state must not be inherited by a duplicate (#1201): copying
        // isMoneyTransferred from a settled event marks the copy as already paid out
        // (blocks refunds, misreports market-sale-stats), and maxCapacityLeft is a
        // live sold-tickets counter that belongs to the source event only.
        delete newEvent.isMoneyTransferred
        delete newEvent.maxCapacityLeft
        try {
            // time manipulations
            if (duplicateEventStartDate && eventToDuplicate.end_date) { // we want to the event time span to the new event so that the new event will be the same time span as the original event but not the same end_date
                const originalEventTimeSpan = new Date(eventToDuplicate.end_date) - new Date(eventToDuplicate.start_date)
                const newEventStartDate = new Date(duplicateEventStartDate)
                const newEventEndDate = new Date(newEventStartDate.getTime() + originalEventTimeSpan)
                newEvent.start_date = newEventStartDate
                newEvent.end_date = newEventEndDate
            }
            // Day from the new (noon-anchored) duplicate date, time from the original
            // start_hour/end_hour — same rule as the create/update flows. combineDayAndTime
            // wraps its inputs in new Date(), so it also handles the API's ISO-string hours
            // (the previous end_hour code called .getHours() on the raw string, which threw
            // and was silently swallowed, leaving end_hour stuck at noon).
            if (duplicateEventStartDate && eventToDuplicate.start_hour) {
                newEvent.start_hour = combineDayAndTime(duplicateEventStartDate, eventToDuplicate.start_hour)
            }

            if (duplicateEventStartDate && eventToDuplicate.end_hour) {
                newEvent.end_hour = combineDayAndTime(duplicateEventStartDate, eventToDuplicate.end_hour)
            }
        } catch (e) {
            console.error('duplicate event time manipulation failed')
            console.error(e.message)
        }

        const eventTicketsToDuplicate = await fetchEventTickets({ accountId, eventId: eventToDuplicate._id })

        const createdEventResponse = await createEvent({ accountId, eventData: newEvent })

        // Duplicating must not clone the capacity ticket onto the copy: it belongs to its own event
        // and the backend creates it on demand (#1002). A free ticket still copies, as it always did.
        const promises = eventTicketsToDuplicate.data.filter((t) => !isBuyerHiddenTicket(t)).map(ticket => {
            delete ticket._id
            ticket.event = newEvent._id
            return createEventTicket({ accountId, eventId: createdEventResponse.data._id, ticketData: ticket })
        })

        await Promise.all(promises)
        return createdEventResponse.data
    },
    async getEventPageData(accountHandler, eventHandler, utm) {

        const accountResponse = await getAccountIdByHandler(accountHandler)
        const accountId = accountResponse.data._id
        const eventResponse = await getEventByHandler({ accountId, eventHandler, utm })
        const eventTickets = await fetchEventTickets({ accountId, eventId: eventResponse.data._id, nimiFeeType: eventResponse.data.feeCollectionType === FeeCollectionTypes.ON_TOP_OF_TICKET ? FeeCollectionTypes.ON_TOP_OF_TICKET : null })

        let relevantLink
        if (utm) {
            try {
                const eventLinkResponse = await getLinksOfEventsByUtm({ accountId, eventId: eventResponse.data._id, utm })
                relevantLink = eventLinkResponse.data
            } catch (e) {
                ErrorService.handleError(e)
                console.log(e);
            }
        }

        return {
            eventData: eventResponse.data,
            accountData: accountResponse.data,
            ticketsData: eventTickets.data.filter(ticket => {

                // FIRST, before anything else: the group-capacity ticket must never reach a buyer.
                // The link branch below returns early on `relevantLink.tickets.includes(_id)`, BEFORE
                // the is_enabled gate, so a hidden ticket on an enabled link IS publicly buyable —
                // which is exactly how free tickets are distributed, and must keep working. Only the
                // capacity ticket is excluded here (#1002); see isBuyerHiddenTicket.
                if (isBuyerHiddenTicket(ticket)) return false

                const { is_enabled, start_date, end_date, _id } = ticket

                if (start_date && isDateGreaterThanToday(start_date)) {
                    return false
                }

                if (end_date && isDateLessThanToday(end_date)) {
                    return false
                }
                // ticket.countTicketsAllowPerPurchase = Math.min(ticket.countTicketsAllowPerPurchase, ticket.ticketsLeft) // TODO - check this after dan will fix it
                // if the event has a utm, we want to add special tickets to the event
                if (relevantLink && relevantLink.is_enabled) {
                    if (relevantLink.is_sold_out) {
                        return false
                    }
                    if (relevantLink.countTicketsAllowPerPurchase) {
                        ticket.countTicketsAllowPerPurchase = Math.min(ticket.countTicketsAllowPerPurchase, relevantLink.countTicketsAllowPerPurchase)
                    }
                    return relevantLink.tickets.includes(_id)
                }

                if (!is_enabled || is_enabled === 'false') {
                    return false
                }

                return true
            }),
            marketplaceData: accountResponse.data.marketPlace,
            linkData: relevantLink
        }

    },
    /**
     * `includeOrders: false` skips the two heavy lists (userEvents + eventOrders).
     * On a big event those are several MB each, so tabs that don't read them
     * (sell-stats, links) must not pay for them.
     */
    async getEventDashboardData({ accountId, eventId, isTimeSlot = false, includeOrders = true, includeExtras = true }) {

        const skipHeavyLists = isTimeSlot || !includeOrders;
        // `includeExtras: false` is for the case where only the order lists are missing —
        // re-requesting tickets/coupons/utms/links we already hold is four wasted round trips.
        const EMPTY = Promise.resolve([]);

        const eventData = await Promise.all([
            skipHeavyLists ? Promise.resolve([]) : getUserEventsOfEvent({ accountId, eventId }).then(({ data }) => data),
            skipHeavyLists ? Promise.resolve([]) : getEventOrdersOfEvent({ accountId, eventId }).then(({ data }) => data),
            includeExtras ? fetchEventTicketsSecure({ accountId, eventId }).then(({ data }) => data) : EMPTY,
            includeExtras ? getEventCoupons({ accountId, eventId }).then(({ data }) => data) : EMPTY,
            includeExtras ? getUtmsOfEvent({ accountId, eventId }).then(({ data }) => data) : EMPTY,
            includeExtras ? getLinksOfEventsSecured({ accountId, eventId }).then(({ data }) => data) : EMPTY,
        ])

        const userEvents = eventData[0]
        const eventOrders = eventData[1]
        const tickets = eventData[2]
        const coupons = eventData[3]
        const utms = eventData[4]
        const links = eventData[5]

        return { userEvents, eventOrders, tickets, coupons, utms, links }

    },
    async updateOrCreateNimiTicketForSeatsIoTicket({ ticket, seatsIoTicketCategory, accountId, eventId, ticketQuantity, }) {
        if (ticket._id) {
            const ticketToUpdate = {
                name: seatsIoTicketCategory.label,
                price: ticket.price,
                quantity: ticketQuantity,
                seatsCategoryKey: seatsIoTicketCategory.key,
                seatsCategoryLabel: seatsIoTicketCategory.label,
                seatsCategoryColor: seatsIoTicketCategory.color,
                priceLevel: ticket.priceLevel,
            }
            return API.updateEventTicket({ accountId, eventId, ticketId: ticket._id, ticketData: ticketToUpdate })
        }

        const ticketDataToCreate = {
            name: seatsIoTicketCategory.label,
            price: ticket.price,
            seatsCategoryKey: seatsIoTicketCategory.key,
            seatsCategoryLabel: seatsIoTicketCategory.label,
            seatsCategoryColor: seatsIoTicketCategory.color,
            quantity: ticketQuantity,
            is_enabled: true,
            start_date: null,
            end_date: null,
            // A chart category ticket can start life without this field (both seed sites in
            // ChosenChartSection — the initial useEffect and addPricingLevel — omit it), and
            // sending undefined lets Mongoose apply its `default: 1` — which the buyer chart
            // then reads as a 1-seat-per-CATEGORY cap whenever the event has no
            // `maxSeatsPerUser` (see resolveMaxSelectedObjects). Fall back to the FE's own
            // per-category default instead. Issue #1130.
            countTicketsAllowPerPurchase: ticket.countTicketsAllowPerPurchase ?? DEFAULT_MAX_PER_CATEGORY,
            description: "Nimi representation for Seats.io tickets",
            currency: "ILS",
            category: "party",
            is_sold_out: false,
            is_deleted: false,
            is_hidden: false,
            is_free: false,
            is_recurring: false,
            priceLevel: ticket.priceLevel,
        }
        return API.createEventTicket({ accountId, eventId, ticketData: ticketDataToCreate })
    },
    async removeNimiTicketForSeatsIoTicket(ticket) {
        return API.deleteTicket({ accountId: ticket.account, eventId: ticket.event, ticketId: ticket._id })
    },

    async bookEvent({ seller, event, endUserSeasonTicket, endUser, selectedTimeSlot }) {
        const eventId = event._id;
        // Handle both populated (object) and unpopulated (string) account field
        const accountId = typeof event.account === 'object' ? event.account?._id : event.account;
        const marketplaceId = seller.marketPlace._id;
        const endUserSeasonTicketId = endUserSeasonTicket._id;
        try {
            // 1. Fetch event tickets to get the ticket data needed for getPrice
            const { data: tickets } = await fetchEventTickets({ accountId, eventId });

            if (!tickets || tickets.length === 0) {
                throw new Error('No tickets found for this event');
            }

            // 2. Assume single ticket type for bookable events (user doesn't select specific tickets)
            const ticket = tickets[0]; // Get the first available ticket
            const ticketsIdsChosen = [ticket._id]; // Single ticket for booking

            // 3. Prepare order data for getPrice API call
            const orderData = {
                event: { _id: eventId }, // Event reference
                ticketsIdsChosen, // Array with single ticket ID
                endUserSeasonTicketId, // User's subscription ID
                // Scope the season-ticket purchase-limit validation to the chosen slot
                // (one purchase per slot, not per event). Without this, getPrice counts
                // per event and wrongly blocks booking a different slot of the same event.
                ...(selectedTimeSlot?.id && { timeSlotId: selectedTimeSlot.id })
            };

            // 4. Call getPrice to validate subscription and get pricing
            const { data: priceData } = await getOrderPrice({ accountId, orderData });

            // 5. If price is 0, proceed with actual booking
            if (priceData.finalPriceWithNimiFee === 0) {
                // UserEventService is already imported at the top of the file

                // 6. Prepare user event data for booking
                const userEventData = {
                    fname: endUser.fname || '',
                    lname: endUser.lname || '',
                    phone: endUser.phone || '',
                    email: endUser.email || '',
                    gender: endUser.gender || '',
                    birth_date: endUser.birth_date || null,
                    social_link: endUser.social_link || '',
                    ticketId: ticket._id,
                    isAllowMarketingDelivery: endUser.isAllowMarketingDelivery || false,
                    businessCode: endUser.businessCode || '',
                    businessName: endUser.businessName || '',
                    endUserSeasonTicketId,
                    // Add time slot information if available.
                    // The userEvent schema stores the slot as timeSlotId/timeSlotDate/timeSlotTime
                    // (HHMM) + timeSlotDuration — there is NO timeSlotStartTime/timeSlotEndTime field,
                    // so writing those names got them silently stripped by Mongoose and the user-event
                    // ended up with an id + date but no time at all.
                    // timeSlotTime must be HHMM (BE validates ^\d{4}$); slot.startTime is the display
                    // form ("15:00"), so derive it from the slot id (YYYY-MM-DD_HHMM) — the same source
                    // createEventOrder splits, keeping order and user-event in agreement.
                    ...(selectedTimeSlot?.id && {
                        timeSlotId: selectedTimeSlot.id,
                        timeSlotDate: selectedTimeSlot.date,
                        timeSlotTime: selectedTimeSlot.id?.split('_')[1],
                        timeSlotDuration: selectedTimeSlot.slotDuration
                    }),
                    ticketsInfo: [{
                        isApproved: false,
                        isCheckedIn: false,
                        isRefunded: false,
                        originalPrice: ticket.price,
                        ticket: ticket._id,
                        // Add time slot to ticket info as well
                        ...(selectedTimeSlot?.id && {
                            timeSlotId: selectedTimeSlot.id,
                            timeSlotDate: selectedTimeSlot.date,
                            timeSlotTime: selectedTimeSlot.id?.split('_')[1],
                            timeSlotDuration: selectedTimeSlot.slotDuration
                        })
                    }]
                };

                // 7. Create user event record
                const savedUserEvents = await UserEventService.createManyUserEvents({
                    accountId,
                    eventId,
                    allUserEvents: [{ userEvent: userEventData, ticketId: ticket._id }]
                });
                // 8. Create event order with FREE_TICKET
                const { data: eventOrder } = await UserEventService.createEventOrder({
                    accountId,
                    eventId,
                    purchaseToken: "FREE_TICKET",
                    userEvents: savedUserEvents,
                    seasonTicket: {
                        endUserSeasonTicket: { _id: endUserSeasonTicketId },
                        endUser: { _id: endUser._id }
                    },
                    seller: seller._id,
                    marketplaceId,
                    // Pass the slot so createEventOrder splits it into timeSlotDate + timeSlotTime
                    // on the order. Without this the fast-booking order persists with a slot id but
                    // no timeSlotTime, and the backend per-slot counter (timeSlots.bookedCount, keyed
                    // by {eventId}_{date}_{startTime}) never increments — the slot then wrongly shows
                    // as empty (0/N) in the admin calendar. Mirrors the event-page checkout path.
                    ...(selectedTimeSlot?.id && {
                        timeSlotId: selectedTimeSlot.id,
                        timeSlotDuration: selectedTimeSlot.slotDuration,
                    }),
                });

                // 9. Return success with booking details
                return {
                    success: true,
                    priceData,
                    ticket,
                    ticketsIdsChosen,
                    canBookForFree: true,
                    userEvent: savedUserEvents[0],
                    eventOrder
                };
            }

            // 10. Return failure if not free
            return {
                success: false,
                priceData,
                ticket,
                ticketsIdsChosen,
                canBookForFree: false
            };

            // 

        } catch (error) {

            let errorMessage;
            try {
                errorMessage = JSON.parse(error.message).data.message.toLowerCase();

            } catch (error) {
                errorMessage = error.message.toLowerCase();
            }
            // Handle specific subscription validation errors
            if (errorMessage) {
                // Map error messages to user-friendly messages
                if (errorMessage.includes("already used for this event")) {
                    throw new Error('SUBSCRIPTION_ALREADY_USED_FOR_EVENT');
                }
                if (errorMessage.includes("season ticket not active or limit is reached")) {
                    throw new Error('SUBSCRIPTION_NOT_ACTIVE_OR_LIMIT_REACHED');
                }
                if (errorMessage.includes("end user season ticket limit reached for this event")) {
                    throw new Error('SUBSCRIPTION_EVENT_LIMIT_REACHED');
                }
                if (errorMessage.includes("end user season ticket limit reached")) {
                    throw new Error('SUBSCRIPTION_OVERALL_LIMIT_REACHED');
                }
                if (errorMessage.includes("end user season ticket not configured for this event")) {
                    throw new Error('SUBSCRIPTION_NOT_CONFIGURED_FOR_EVENT');
                }
            }

            // Re-throw the error for general handling
            throw error;
        }
    },
}

const getDesignColorsFromEventData = (eventData) => {
    const { eventBackground, eventButtons, eventProgressBar, eventPrivacyPolicy } = eventData
    return {
        colors: {
            background: eventBackground,
            buttons: eventButtons,
            progressBar: eventProgressBar,
            privacyPolicy: eventPrivacyPolicy
        }
    }
}

const mapDesignColorsToEventFrom = (design) => {
    const { colors = {} } = design
    const { background, buttons, progressBar, privacyPolicy } = colors
    return {
        eventBackground: background,
        eventButtons: buttons,
        eventProgressBar: progressBar,
        eventPrivacyPolicy: privacyPolicy
    }
}